import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SplitFactory } from '@splitsoftware/splitio';
import { Attributes, IClient, ISDK } from '@splitsoftware/splitio/types/splitio';
import { BehaviorSubject, fromEvent, Observable, Subscription } from 'rxjs';

import { getOverride } from '@app/env-helpers';
import { environment } from '@app/env-service';
import {
  FEATURE_FLAGS,
  FeatureFlagValue,
  FeatureHash,
} from '@app/feature-toggle-list';

//

@Injectable({
  providedIn: 'root',
})
export class FeatureToggleService {
  url :string = environment.url;
  splitio :ISDK;
  splitClient :IClient;
  subscription :Subscription;
  currentKey :string = '';
  private readonly _featureToggle = new BehaviorSubject<FeatureHash>(
    <FeatureHash>{},
  );
  public featureToggles$ = this._featureToggle.asObservable();
  public featureToggles = <FeatureHash>{};

  constructor(private http :HttpClient) {}

  async initSdk(key :string) {
    // no key
    // no change
    if (
      !key ||
      key === this.currentKey ||
      (typeof this.currentKey === 'number' && typeof key !== 'number')
    ) {
      return;
    }
    this.currentKey = key;

    if (this.splitClient) {
      await this.splitClient.destroy();
    }

    const prodMatch = location.hostname.match(
      /([^.]+)\.(luminousolutions|joinluminous)\.com/,
    );
    const isProd = prodMatch && prodMatch[1] !== 'staging';
    this.splitio = SplitFactory({
      core: {
        authorizationKey: isProd
          ? '9hehsbo1sm7sjtfcjpn3gukqu19pogeog6np'
          : 'uvt3a39378kv64n2n21tdqhl5u49jbe7b5ki',
        key,
      },
      features: {},
    });
    this.splitClient = this.splitio.client();

    // verify if sdk is initialized
    try {
      await this.verifyReady();
    } catch (e) {}
  }
  /**
   * Function to check if the SDK is ready, subscribe to an Observable
   * and set the isReady flag according to the result.
   *
   * @returns void
   */
  private async verifyReady() {
    return new Promise<void>((resolve, reject) => {
      const isReadyEvent = fromEvent(
        this.splitClient,
        this.splitClient.Event.SDK_READY,
      );

      this.subscription = isReadyEvent.subscribe({
        next: () => {
          this.getTreatments();
          resolve();
        },
        error(err) {
          console.error('Sdk error: ', err);
          this.unsubscribeSDK();
          reject();
        },
      });
    });
  }
  /**
   * Function to get and set the treatments from the Split API.
   *
   * @returns void
   */
  getTreatments() :void {
    const tenantId :string = environment.tenantId;
    const attributes :Attributes = {
      tenant_id: tenantId,
    };
    const treatments = this.splitClient.getTreatments(
      [...FEATURE_FLAGS],
      attributes,
    );

    this.featureToggles = Object.keys(treatments).reduce(
      (acc, next) => ({
        ...acc,
        [next]: treatments[next] === 'on',
      }),
      <FeatureHash>{},
    );
    this._featureToggle.next(this.featureToggles);
  }

  isActive(flag :FeatureFlagValue) :boolean {
    try {
      const overrides = getOverride().toggles;
      if (typeof overrides[flag] === 'boolean') {
        return overrides[flag];
      }

      return this.featureToggles[flag] || false;
    } catch (e) {
      console.warn(`Unrecognized flag: ${JSON.stringify(flag)}`);

      return false;
    }
  }
  /** @deprecated Use {@link FeatureToggleService#isActive} instead. */
  featureEnabled(key :FeatureFlagValue) :boolean {
    return this.isActive(key);
  }

  /**
   * Function to unsubscribe the Observable from the SDK initialization.
   *
   * @returns void
   */
  unsubscribeSDK() :void {
    this.subscription.unsubscribe();
  }

  getTenantFeatureFlags(selectedTenant :string) :Observable<any> {
    const apiUrl = `https://${selectedTenant}.api.xjimports.com/api/feature_flags`;

    return this.http.get(apiUrl);
  }

  updateTenantFeatureFlags(featureFlags :any) :Observable<any> {
    return this.http.post(`${this.url}/feature-flags`, featureFlags);
  }
}
